import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TournamentDraft } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import { Trophy } from "lucide-react";

const schema = z
  .object({
    name: z.string().trim().min(1, "Enter a tournament name").max(200),
    start_date: z.union([z.literal(""), z.string().date("Enter a valid date")]),
    end_date: z.union([z.literal(""), z.string().date("Enter a valid date")]),
    location: z.string().max(500),
    division: z.string().max(100),
    competition_format: z.string().max(1000),
    status: z.enum(["upcoming", "in_progress", "completed"]),
    outcome: z.enum([
      "",
      "champions",
      "finalist",
      "semi_finalist",
      "third_place",
      "group_stage",
    ]),
  })
  .refine((v) => !v.start_date || !v.end_date || v.end_date >= v.start_date, {
    path: ["end_date"],
    message: "End date must follow start date",
  })
  .refine((v) => !v.outcome || v.status === "completed", {
    path: ["outcome"],
    message: "A finish requires Completed status",
  });
export function TournamentEditor({
  draft,
  onStage,
  onCancel,
}: {
  draft?: TournamentDraft;
  onStage: (draft: TournamentDraft) => void;
  onCancel: () => void;
}) {
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: draft?.name ?? "",
      start_date: draft?.start_date ?? "",
      end_date: draft?.end_date ?? "",
      location: draft?.location ?? "",
      division: draft?.division ?? "",
      competition_format: draft?.competition_format ?? "",
      status: draft?.status ?? "upcoming",
      outcome: draft?.outcome ?? "",
    },
  });
  return (
    <section
      className="tournament-editor"
      aria-label={draft?.id ? "Edit tournament" : "New tournament"}
    >
      <div className="tournament-editor-heading">
        <h3>{draft?.id ? "Edit tournament" : "New tournament"}</h3>
        <Trophy size={18} aria-hidden="true" />
      </div>
      <p className="text-sm text-ink-muted">
        {draft?.id
          ? "Changes apply to every match linked to this tournament."
          : "Create once, then reuse it for each match in this team’s season."}
      </p>
      <div className="tournament-editor-fields">
        <label className="grid gap-1 text-sm tournament-editor-full">
          Tournament name *
          <input
            {...form.register("name")}
            autoFocus
            placeholder="e.g. Harvest Cup"
            aria-invalid={!!form.formState.errors.name}
          />
          {form.formState.errors.name?.message ? (
            <span role="alert" className="text-danger">
              {form.formState.errors.name.message}
            </span>
          ) : null}
        </label>
        {(
          [
            ["start_date", "Start date"],
            ["end_date", "End date"],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="grid gap-1 text-sm">
            {label}
            <Controller
              control={form.control}
              name={key}
              render={({ field }) => (
                <TkticDateInput value={field.value} onChange={field.onChange} />
              )}
            />
            {form.formState.errors[key]?.message ? (
              <span role="alert">{form.formState.errors[key]?.message}</span>
            ) : null}
          </label>
        ))}
        <label className="grid gap-1 text-sm">
          Tournament venue / city
          <input {...form.register("location")} />
        </label>
        <label className="grid gap-1 text-sm">
          Division / age group
          <input
            {...form.register("division")}
            placeholder="e.g. U14 · Division 2"
          />
        </label>
        <label className="grid gap-1 text-sm tournament-editor-full">
          Tournament format
          <input
            {...form.register("competition_format")}
            placeholder="e.g. Two groups of four, then knockout"
          />
        </label>
        <label className="grid gap-1 text-sm">
          Tournament status
          <Controller
            control={form.control}
            name="status"
            render={({ field }) => (
              <TkticSelect
                ariaLabel="Tournament status"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: "upcoming", label: "Upcoming" },
                  { value: "in_progress", label: "In progress" },
                  { value: "completed", label: "Completed" },
                ]}
              />
            )}
          />
        </label>
        <label className="grid gap-1 text-sm">
          Overall finish
          <Controller
            control={form.control}
            name="outcome"
            render={({ field }) => (
              <TkticSelect
                ariaLabel="Tournament finish"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: "", label: "Not recorded" },
                  { value: "champions", label: "Champions" },
                  { value: "finalist", label: "Finalist" },
                  { value: "semi_finalist", label: "Semi-finalist" },
                  { value: "third_place", label: "Third place" },
                  { value: "group_stage", label: "Group stage" },
                ]}
              />
            )}
          />
          {form.formState.errors.outcome?.message ? (
            <span role="alert">{form.formState.errors.outcome.message}</span>
          ) : null}
        </label>
      </div>
      <div className="tournament-editor-actions">
        <Button
          type="button"
          onClick={() =>
            void form.handleSubmit((values) =>
              onStage({
                ...values,
                id: draft?.id,
                version: draft?.version,
                outcome: values.outcome || null,
              }),
            )()
          }
        >
          Use tournament
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel tournament changes
        </Button>
      </div>
      <p className="match-editor-hint">
        Tournament changes save together with this match.
      </p>
    </section>
  );
}
